import React from 'react';

import { SubtaskProgressIndicator } from './board/SubtaskProgressIndicator';
import type { CustomFieldDefinition } from '../state/active-board-context';

export interface MiniCardProps {
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly labels: { name: string; color: string }[];
  readonly memberAvatars: string[];
  readonly memberNames?: string[];
  readonly dueDate: string | null;
  readonly coverImage: string | null;
  readonly checklistProgress: { completed: number; total: number } | null;
  readonly subtaskProgress: { completed: number; total: number } | null;
  readonly customFields?: Record<string, boolean | string | string[]>;
  readonly customFieldDefinitions?: readonly CustomFieldDefinition[];
  readonly description?: string;
  readonly showDescriptionOnCard?: boolean;
  readonly onClick: (cardId: string) => void;
}

/**
 * MiniCard — compact card representation in the board view.
 * Shows title, card number, labels, member avatars, due date indicator,
 * cover image, and checklist progress.
 */
export function MiniCard({
  cardId,
  cardNumber,
  title,
  labels,
  memberAvatars,
  memberNames,
  dueDate,
  coverImage,
  checklistProgress,
  subtaskProgress,
  customFields,
  customFieldDefinitions,
  description,
  showDescriptionOnCard,
  onClick,
}: MiniCardProps): React.ReactElement {
  const dueDateStatus = getDueDateStatus(dueDate);

  const visibleCustomFields = getVisibleCustomFields(customFields, customFieldDefinitions);

  return (
    <article
      role="listitem"
      data-card-id={cardId}
      className="bg-card text-card-foreground rounded-md shadow-sm border border-border p-3 cursor-pointer hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-ring"
    >
      <button
        onClick={() => onClick(cardId)}
        className="w-full text-left focus:outline-none"
        aria-label={`Card #${cardNumber}: ${title}`}
      >
        {coverImage && (
          <img
            src={coverImage}
            alt=""
            className="w-full h-24 object-cover rounded mb-2"
          />
        )}

        {labels.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {labels.map((label) => (
              <span
                key={label.name}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium text-white"
                style={{ backgroundColor: label.color }}
              >
                {label.name}
              </span>
            ))}
          </div>
        )}

        <p className="text-sm font-medium text-card-foreground">{title}</p>

        {showDescriptionOnCard && description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{description}</p>
        )}

        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span className="font-mono">#{cardNumber}</span>

          <div className="flex items-center gap-2">
            {checklistProgress && (
              <span aria-label={`Checklist: ${checklistProgress.completed} of ${checklistProgress.total} complete`}>
                ☑ {checklistProgress.completed}/{checklistProgress.total}
              </span>
            )}

            {subtaskProgress && (
              <SubtaskProgressIndicator
                completed={subtaskProgress.completed}
                total={subtaskProgress.total}
              />
            )}

            {dueDateStatus && (
              <DueDateBadge status={dueDateStatus} date={dueDate!} />
            )}
          </div>
        </div>

        {(memberAvatars.length > 0 || (memberNames && memberNames.length > 0)) && (
          <div className="flex items-center -space-x-1.5 mt-2" aria-label={`Assigned to: ${memberNames?.join(', ') ?? ''}`}>
            {(memberNames ?? []).slice(0, 3).map((name, i) => (
              <MemberAvatar key={i} name={name} avatarUrl={memberAvatars[i] ?? null} />
            ))}
            {(memberNames ?? []).length > 3 && (
              <span
                className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-card bg-muted text-[10px] font-medium text-muted-foreground"
                title={memberNames?.slice(3).join(', ')}
              >
                +{memberNames!.length - 3}
              </span>
            )}
          </div>
        )}

        {visibleCustomFields.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2" aria-label="Custom fields">
            {visibleCustomFields.map(({ definition, value }) => (
              <CustomFieldBadge key={definition.fieldId} definition={definition} value={value} />
            ))}
          </div>
        )}
      </button>
    </article>
  );
}

type DueDateStatus = 'upcoming' | 'overdue' | null;

function MemberAvatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }): React.ReactElement {
  const [imgFailed, setImgFailed] = React.useState(false);

  if (avatarUrl && !imgFailed) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        title={name}
        className="w-6 h-6 rounded-full border-2 border-card"
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <span
      title={name}
      className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-card bg-primary text-[10px] font-semibold text-primary-foreground"
    >
      {getInitials(name)}
    </span>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getDueDateStatus(dueDate: string | null): DueDateStatus {
  if (!dueDate) return null;
  const due = new Date(dueDate).getTime();
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000;

  if (now > due) return 'overdue';
  if (due - now <= twentyFourHours) return 'upcoming';
  return null;
}

function DueDateBadge({ status, date }: { status: 'upcoming' | 'overdue'; date: string }): React.ReactElement {
  const formatted = new Date(date).toLocaleDateString();
  const className = status === 'overdue'
    ? 'text-danger-600 font-medium'
    : 'text-warning-600 font-medium';

  return (
    <span className={className} aria-label={`Due ${formatted}${status === 'overdue' ? ' (overdue)' : ' (due soon)'}`}>
      {status === 'overdue' ? '🔴' : '🟡'} {formatted}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Custom Field Helpers
// ---------------------------------------------------------------------------

interface VisibleField {
  readonly definition: CustomFieldDefinition;
  readonly value: boolean | string | string[];
}

function getVisibleCustomFields(
  customFields?: Record<string, boolean | string | string[]>,
  definitions?: readonly CustomFieldDefinition[],
): VisibleField[] {
  if (!customFields || !definitions) return [];

  const hasValues = Object.keys(customFields).length > 0;
  if (!hasValues) return [];

  return definitions
    .filter((def) => def.showOnCard && customFields[def.fieldId] !== undefined)
    .sort((a, b) => a.sort - b.sort)
    .slice(0, 3)
    .map((def) => ({ definition: def, value: customFields[def.fieldId] }));
}

function CustomFieldBadge({
  definition,
  value,
}: {
  readonly definition: CustomFieldDefinition;
  readonly value: boolean | string | string[];
}): React.ReactElement | null {
  switch (definition.fieldType) {
    case 'boolean':
      return (
        <span
          className="inline-flex items-center text-xs text-muted-foreground"
          aria-label={`${definition.name}: ${value ? 'checked' : 'unchecked'}`}
        >
          {value ? (
            <span className="text-green-600" aria-hidden="true">✓</span>
          ) : (
            <span className="text-gray-400" aria-hidden="true">○</span>
          )}
          <span className="ml-0.5">{definition.name}</span>
        </span>
      );

    case 'dropdown-single':
      if (typeof value !== 'string' || !value) return null;
      return (
        <span
          className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
          aria-label={`${definition.name}: ${value}`}
        >
          {value}
        </span>
      );

    case 'dropdown-multi':
      if (!Array.isArray(value) || value.length === 0) return null;
      return (
        <>
          {value.map((option) => (
            <span
              key={option}
              className="inline-flex items-center text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
              aria-label={`${definition.name}: ${option}`}
            >
              {option}
            </span>
          ))}
        </>
      );

    case 'text':
      if (typeof value !== 'string' || !value) return null;
      const truncated = value.length > 30 ? `${value.slice(0, 30)}…` : value;
      return (
        <span
          className="inline-flex items-center text-xs text-muted-foreground truncate max-w-[120px]"
          title={value}
          aria-label={`${definition.name}: ${value}`}
        >
          {truncated}
        </span>
      );

    case 'number':
      if (typeof value !== 'string' || !value) return null;
      return (
        <span
          className="inline-flex items-center text-xs text-muted-foreground font-mono"
          aria-label={`${definition.name}: ${value}`}
        >
          {definition.name}: {value}
        </span>
      );

    default:
      return null;
  }
}
